/**
 * ScrollReveal — Cards animate in on viewport entry.
 *
 * Uses `useInView` from motion/react. Spring-based, once by default.
 * No-op when prefers-reduced-motion is active.
 */

import { useRef, type ReactNode } from 'react';
import { motion, useInView } from 'motion/react';

interface ScrollRevealProps {
  children: ReactNode;
  delay?: number;
  direction?: 'up' | 'left' | 'right';
  once?: boolean;
  amount?: number;
  className?: string;
}

const prefersReduced =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const offsets = {
  up: { x: 0, y: 20 },
  left: { x: -20, y: 0 },
  right: { x: 20, y: 0 },
} as const;

export function ScrollReveal({
  children,
  delay = 0,
  direction = 'up',
  once = true,
  amount = 0.2,
  className,
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once, amount });

  if (prefersReduced) {
    return <div className={className}>{children}</div>;
  }

  const { x: ox, y: oy } = offsets[direction];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: ox, y: oy }}
      animate={inView ? { opacity: 1, x: 0, y: 0 } : { opacity: 0, x: ox, y: oy }}
      transition={{
        type: 'spring',
        stiffness: 350,
        damping: 30,
        delay,
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
